import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Moon, Zap, Flame, Heart, X } from 'lucide-react';
import { WorkoutSession } from '@/lib/types';

interface MacroDay {
  protein: number;
  carbs: number;
  fat: number;
  calories: number;
}

interface MacroTargets {
  protein: number;
  carbs: number;
  fat: number;
  calories: number;
}

interface DayStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateStr: string; // YYYY-MM-DD
  session: (WorkoutSession & {
    watchCalories?: number | null;
    watchAvgHr?: number | null;
    watchMaxHr?: number | null;
    sessionFeeling?: string | null;
    recoveryNudge?: string | null;
  }) | null;
  macros: MacroDay | null;
  macroTargets: MacroTargets;
  exerciseNames: Record<string, string>; // exerciseId → name
}

const feelingEmoji: Record<string, string> = {
  crushed_it: '🔥',
  solid: '✅',
  tough: '😤',
  cooked: '💀',
};

const feelingLabel: Record<string, string> = {
  crushed_it: 'Crushed It',
  solid: 'Solid Session',
  tough: 'Tough One',
  cooked: 'Cooked',
};

function getMuscleGroupTag(name: string): { label: string; color: string } | null {
  const n = name.toLowerCase();
  if (n.includes('push') || n.includes('chest') || n.includes('tricep')) return { label: 'PUSH', color: 'bg-orange-400/20 text-orange-300 border-orange-400/40' };
  if (n.includes('pull') || n.includes('back') || n.includes('bicep') || n.includes('row')) return { label: 'PULL', color: 'bg-blue-400/20 text-blue-300 border-blue-400/40' };
  if (n.includes('leg') || n.includes('squat') || n.includes('deadlift') || n.includes('glute') || n.includes('quad') || n.includes('hamstring')) return { label: 'LEGS', color: 'bg-red-400/20 text-red-300 border-red-400/40' };
  if (n.includes('arm') || n.includes('curl') || n.includes('press') || n.includes('military') || n.includes('shoulder') || n.includes('delt')) return { label: 'ARMS', color: 'bg-teal-400/20 text-teal-300 border-teal-400/40' };
  if (n.includes('full') || n.includes('total') || n.includes('body')) return { label: 'FULL', color: 'bg-purple-400/20 text-purple-300 border-purple-400/40' };
  return null;
}

function sessionHasPR(session: WorkoutSession): boolean {
  return (session.plannedExercises as any[]).some((ex: any) =>
    (ex.sets as any[]).some((set: any) => set.isPR === true || set.isNewPR === true));
}

export default function DayStatsModal({ isOpen, onClose, dateStr, session, macros, macroTargets, exerciseNames }: DayStatsModalProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragCurrentY = useRef<number>(0);

  // Swipe-down to dismiss
  useEffect(() => {
    if (!isOpen) return;
    const el = sheetRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      dragStartY.current = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (dragStartY.current === null) return;
      const dy = e.touches[0].clientY - dragStartY.current;
      dragCurrentY.current = dy;
      if (dy > 0) {
        el.style.transform = `translateY(${dy}px)`;
        el.style.transition = 'none';
      }
    };
    const onTouchEnd = () => {
      if (dragCurrentY.current > 80) {
        onClose();
      } else {
        el.style.transform = '';
        el.style.transition = 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)';
      }
      dragStartY.current = null;
      dragCurrentY.current = 0;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isOpen, onClose]);

  // Reset transform on open
  useEffect(() => {
    if (isOpen && sheetRef.current) {
      sheetRef.current.style.transform = '';
      sheetRef.current.style.transition = 'transform 0.35s cubic-bezier(0.23, 1, 0.32, 1)';
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dateObj = new Date(dateStr + 'T00:00:00');
  const fullDate = format(dateObj, 'EEEE, MMMM d');

  const isCompleted = session && !!session.completedAt;
  const feeling = (session as any)?.sessionFeeling as string | null ?? null;
  const recoveryNudge = (session as any)?.recoveryNudge as string | null ?? null;
  const muscleTag = isCompleted ? getMuscleGroupTag(session!.name) : null;
  const hasPR = isCompleted ? sessionHasPR(session!) : false;
  const watchCal = (session as any)?.watchCalories as number | null ?? null;
  const watchHr = (session as any)?.watchAvgHr as number | null ?? null;

  // Training stats
  const completedSets = isCompleted
    ? (session!.plannedExercises as any[]).reduce((acc: number, ex: any) =>
        acc + (ex.sets as any[]).filter((s: any) => s.completed).length, 0)
    : 0;
  const totalVolume = isCompleted
    ? (session!.plannedExercises as any[]).reduce((acc: number, ex: any) =>
        acc + (ex.sets as any[]).filter((s: any) => s.completed).reduce((v: number, s: any) => v + (s.weight || 0) * (s.reps || 0), 0), 0)
    : 0;

  // Exercise list with top set
  const exerciseList = isCompleted
    ? (session!.plannedExercises as any[]).map((ex: any) => {
        const completedSetsList = (ex.sets as any[]).filter((s: any) => s.completed);
        const topSet = completedSetsList.reduce((best: any, s: any) => {
          if (!best) return s;
          return (s.weight || 0) > (best.weight || 0) ? s : best;
        }, null);
        const name = exerciseNames[ex.exerciseId] || ex.exerciseName || ex.exerciseId || 'Unknown';
        return { name, topSet, setCount: completedSetsList.length };
      }).filter((e: any) => e.setCount > 0)
    : [];

  // Macro progress
  const pPct = macros && macroTargets.protein > 0 ? Math.min((macros.protein / macroTargets.protein) * 100, 100) : 0;
  const cPct = macros && macroTargets.carbs > 0 ? Math.min((macros.carbs / macroTargets.carbs) * 100, 100) : 0;
  const fPct = macros && macroTargets.fat > 0 ? Math.min((macros.fat / macroTargets.fat) * 100, 100) : 0;
  const calPct = macros && macroTargets.calories > 0 ? Math.min((macros.calories / macroTargets.calories) * 100, 100) : 0;

  const pOver = macros ? macros.protein > macroTargets.protein : false;
  const cOver = macros ? macros.carbs > macroTargets.carbs : false;
  const fOver = macros ? macros.fat > macroTargets.fat : false;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        style={{ animation: 'fadeIn 0.2s ease-out' }}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl border-t border-border/60 bg-background shadow-2xl"
        style={{
          maxHeight: '88vh',
          animation: 'slideUp 0.35s cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-4 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-5 pb-8" style={{ maxHeight: 'calc(88vh - 40px)' }}>

          {/* ── Header ── */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-mono uppercase text-muted-foreground tracking-widest">{fullDate}</p>
              {isCompleted && (
                <h2 className="text-base font-mono font-bold text-foreground mt-0.5 leading-tight">{session!.name}</h2>
              )}
              {!isCompleted && !session && (
                <h2 className="text-base font-mono font-bold text-muted-foreground mt-0.5 flex items-center gap-1.5">
                  <Moon className="h-4 w-4" /> Rest Day
                </h2>
              )}
              {session && !isCompleted && (
                <h2 className="text-base font-mono font-bold text-primary/70 mt-0.5">{session.name} <span className="text-[10px] text-muted-foreground">(PLANNED)</span></h2>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              {feeling && (
                <span className="text-xl" title={feelingLabel[feeling] ?? feeling}>{feelingEmoji[feeling]}</span>
              )}
              {hasPR && (
                <span title="PR Hit!">
                  <Zap className="h-4 w-4 text-yellow-400 fill-yellow-400 shrink-0" />
                </span>
              )}
            </div>
          </div>

          {/* Muscle group + feeling label */}
          {isCompleted && (muscleTag || feeling) && (
            <div className="flex items-center gap-2 mb-4">
              {muscleTag && (
                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${muscleTag.color}`}>
                  {muscleTag.label}
                </span>
              )}
              {feeling && (
                <span className="text-[10px] font-mono text-muted-foreground">{feelingLabel[feeling]}</span>
              )}
            </div>
          )}

          {/* ── Training Summary ── */}
          {isCompleted && (
            <div className="mb-5">
              <p className="text-[9px] font-mono uppercase text-muted-foreground/60 tracking-widest mb-2">Training Summary</p>

              {/* Sets + Volume */}
              <div className="flex gap-4 mb-3">
                <div className="flex flex-col">
                  <span className="text-xl font-mono font-bold text-primary">{completedSets}</span>
                  <span className="text-[9px] font-mono text-muted-foreground uppercase">Sets</span>
                </div>
                <div className="w-px bg-border/40" />
                <div className="flex flex-col">
                  <span className="text-xl font-mono font-bold text-foreground">
                    {totalVolume >= 1000 ? `${(totalVolume / 1000).toFixed(1)}k` : totalVolume.toLocaleString()}
                  </span>
                  <span className="text-[9px] font-mono text-muted-foreground uppercase">lbs</span>
                </div>
              </div>

              {/* Exercise list */}
              {exerciseList.length > 0 && (
                <div className="space-y-1.5 border border-border/30 rounded-lg p-3 bg-secondary/10">
                  {exerciseList.map((ex: any, i: number) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-[11px] font-mono text-foreground/90 truncate max-w-[55%]">{ex.name}</span>
                      {ex.topSet && (
                        <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                          {ex.topSet.weight ? `${ex.topSet.weight} lbs × ${ex.topSet.reps}` : `${ex.topSet.reps} reps`}
                          {ex.setCount > 1 && <span className="text-muted-foreground/50 ml-1">+{ex.setCount - 1}</span>}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Nutrition Summary ── */}
          <div className="mb-5">
            <p className="text-[9px] font-mono uppercase text-muted-foreground/60 tracking-widest mb-2">Nutrition</p>
            {macros ? (
              <div className="space-y-2">
                {/* Protein */}
                <div>
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-[10px] font-mono text-blue-400">Protein</span>
                    <span className={`text-[10px] font-mono ${pOver ? 'text-orange-400' : 'text-muted-foreground'}`}>
                      {macros.protein}g / {macroTargets.protein}g
                    </span>
                  </div>
                  <div className="h-1.5 bg-secondary/40 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${pOver ? 'bg-orange-400' : 'bg-blue-400'}`} style={{ width: `${pPct}%` }} />
                  </div>
                </div>
                {/* Carbs */}
                <div>
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-[10px] font-mono text-yellow-400">Carbs</span>
                    <span className={`text-[10px] font-mono ${cOver ? 'text-orange-400' : 'text-muted-foreground'}`}>
                      {macros.carbs}g / {macroTargets.carbs}g
                    </span>
                  </div>
                  <div className="h-1.5 bg-secondary/40 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${cOver ? 'bg-orange-400' : 'bg-yellow-400'}`} style={{ width: `${cPct}%` }} />
                  </div>
                </div>
                {/* Fat */}
                <div>
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-[10px] font-mono text-red-400">Fat</span>
                    <span className={`text-[10px] font-mono ${fOver ? 'text-orange-400' : 'text-muted-foreground'}`}>
                      {macros.fat}g / {macroTargets.fat}g
                    </span>
                  </div>
                  <div className="h-1.5 bg-secondary/40 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${fOver ? 'bg-orange-400' : 'bg-red-400'}`} style={{ width: `${fPct}%` }} />
                  </div>
                </div>
                {/* Calories */}
                <div className="pt-1 border-t border-border/30">
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-[10px] font-mono text-foreground/70">Calories</span>
                    <span className="text-[10px] font-mono text-foreground/70">
                      {macros.calories.toLocaleString()} / {macroTargets.calories.toLocaleString()} kcal
                    </span>
                  </div>
                  <div className="h-1.5 bg-secondary/40 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${macros.calories > macroTargets.calories ? 'bg-orange-400' : 'bg-emerald-400'}`}
                      style={{ width: `${calPct}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-[11px] font-mono text-muted-foreground/40 italic">No nutrition logged</p>
            )}
          </div>

          {/* ── Wearable Data ── */}
          {(watchCal || watchHr) && (
            <div className="mb-5">
              <p className="text-[9px] font-mono uppercase text-muted-foreground/60 tracking-widest mb-2">Wearable</p>
              <div className="flex gap-5">
                {watchCal && (
                  <div className="flex items-center gap-1.5">
                    <Flame className="h-4 w-4 text-orange-400" />
                    <div>
                      <span className="text-base font-mono font-bold text-orange-400">{watchCal.toLocaleString()}</span>
                      <span className="text-[9px] font-mono text-muted-foreground ml-1">cal burned</span>
                    </div>
                  </div>
                )}
                {watchHr && (
                  <div className="flex items-center gap-1.5">
                    <Heart className="h-4 w-4 text-red-400" />
                    <div>
                      <span className="text-base font-mono font-bold text-red-400">{watchHr}</span>
                      <span className="text-[9px] font-mono text-muted-foreground ml-1">bpm avg</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Recovery Nudge ── */}
          {recoveryNudge && (
            <div className="mb-4 p-3 rounded-lg border border-primary/20 bg-primary/5">
              <p className="text-[9px] font-mono uppercase text-primary/60 tracking-widest mb-1">Recovery Insight</p>
              <p className="text-[11px] font-mono text-foreground/80 leading-relaxed">{recoveryNudge}</p>
            </div>
          )}

          {/* Rest day minimal view */}
          {!session && !macros && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground/40">
              <Moon className="h-8 w-8 mb-2" />
              <p className="text-xs font-mono uppercase tracking-widest">Rest Day</p>
            </div>
          )}

        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
    </>
  );
}
